import { endOfDay, isSameDay, startOfDay } from 'date-fns';
import { FirestoreDataConverter } from 'firebase/firestore';
import uniqBy from 'lodash/uniqBy';
import { IconFormNames } from 'shared/assets/icons';
import { RRule } from 'shared/lib/rrule';
import { Habit, NewHabit } from 'shared/types/habit';
import {
  CompletionTargetUnit,
  HabitSchedule,
  ScheduleEntry,
  ScheduleEntryState,
} from 'shared/types/habit-schedule';
import {
  goalInboxID,
  goalOtherID,
  lifeAreaOtherID,
} from 'shared/types/orderable-section';
import { NewTask, Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';

import {
  firebaseObjectTimestampToDate,
  firebaseTimestampToDate,
} from './firebase-timestamp-to-date';

export const taskConverter: FirestoreDataConverter<Task> = {
  fromFirestore: (snapshot) => {
    const data = firebaseObjectTimestampToDate(snapshot.data());

    let parsedData: Partial<Task> = {
      type: TaskType.Task,
      createdAt: data.createdAt ?? new Date(),
      updatedAt: data.updatedAt ?? new Date(),
      startDate:
        (data.startDate ? data.startDate : data.createdAt) ?? new Date(),
      reminderTime: data.reminderTime ?? null,
      iconName: data.iconName ?? null,
      schedules: null,
    };

    if (
      [TaskType.Habit, TaskType.Repeating].includes(data.type) &&
      data.schedules?.length
    ) {
      parsedData = parsedData as unknown as Habit;
      parsedData.type = data.type;
      parsedData.iconName = data.iconName ?? IconFormNames.Repeat;
      parsedData.schedules = (
        data.schedules as HabitSchedule[]
      ).map<HabitSchedule>((schedule) => ({
        ...schedule,
        rrule: schedule.rrule
          ? {
              ...schedule.rrule,
              format: RRule.sanitize(schedule.rrule.format),
            }
          : schedule.rrule,
        startDate: schedule.startDate ?? new Date(),
        endDate: firebaseTimestampToDate(schedule.endDate),
        entries: (
          [
            ...(schedule.entries ?? []),
            ...(schedule.completions?.map((date) =>
              schedule.entries?.find((entry) => isSameDay(date, entry.date))
                ? undefined
                : {
                    id: `${date}`,
                    date: date,
                    state: ScheduleEntryState.Complete,
                    value: 1,
                  },
            ) ?? []),
            ...(schedule.skips?.map((date) =>
              schedule.entries?.find((entry) => isSameDay(date, entry.date))
                ? undefined
                : {
                    id: `${date}`,
                    date: date,
                    state: ScheduleEntryState.Skip,
                    value: 1,
                  },
            ) ?? []),
          ] as ScheduleEntry[]
        )
          .filter(Boolean)
          .sort(
            (entryA, entryB) => entryA.date.getTime() - entryB.date.getTime(),
          ),
        plannedOccurrences: uniqBy(
          schedule.plannedOccurrences ?? [],
          // make sure a date is only once present in the planned occurrences
          ({ date }) => (date ? startOfDay(date).getTime() : null),
        ),
        completionTarget: {
          // @ts-ignore -- spread overwriting is explicitly meant like this
          count: 1,
          // @ts-ignore -- spread overwriting is explicitly meant like this
          unit: CompletionTargetUnit.Day,
          ...(schedule.completionTarget ?? {}),
        },
      }));
    }

    return {
      ...data,
      ...parsedData,
    } as Task;
  },
  toFirestore: (task) => task,
};

const excludedGoalIds = [goalInboxID, goalOtherID];

export const newTaskConverter = (task: NewTask) => {
  const deadline = task.endStrategy?.deadline;

  return {
    ...task,
    type: task.schedules?.length
      ? [TaskType.Repeating, TaskType.Habit].includes(task.type)
        ? task.type
        : TaskType.Repeating
      : TaskType.Task,
    iconName: (task as NewHabit).iconName ?? null,
    createdAt: new Date(),
    startDate: task.startDate ? new Date(task.startDate) : new Date(),
    duration: null,
    frozenAt: null,
    completedAt: null,
    archivedAt: null,
    goalId: excludedGoalIds.includes(task.goalId!) ? null : task.goalId ?? null,
    lifeAreaId:
      task.lifeAreaId === lifeAreaOtherID ? null : task.lifeAreaId ?? null,
    reminderTime: task.reminderTime ?? null,
    priority: task.priority ?? null,
    endStrategy: {
      deadline: deadline ? endOfDay(deadline) : null,
      completionCount: task.endStrategy?.completionCount ?? null,
    },
    schedules: task.schedules?.length
      ? task.schedules.map((schedule) => ({
          ...schedule,
          startDate: schedule.startDate
            ? new Date(schedule.startDate)
            : new Date(),
          endDate: schedule.endDate ? new Date(schedule.endDate) : null,
          entries: schedule.entries?.map((entry) => ({
            ...entry,
            date: entry.date ? new Date(entry.date) : null,
          })),
        }))
      : null,
  };
};
